/**
 * 지금 어디를 보여줄까 — 여행 흐름의 미니지도가 무엇을 그릴지 고릅니다.
 *
 * 여행은 시간이 지나면서 있을 곳이 바뀝니다. 출발 전에는 집결지를 보여 줘야 하고,
 * 그 시각이 지나면 그날 갈 곳을, 밤에는 묵을 곳을 보여 주는 편이 자연스럽습니다.
 * 예전에는 늘 "고른 Day 의 구역" 만 그려서, 무엇을 위한 지도인지 알 수 없었습니다.
 *
 * 고르는 순서
 *   1. 집결 시각 전이면        집결지
 *   2. 오늘이 여행 중이면      지금 지난 장소 → 아직이면 그날 첫 장소 → 없으면 그날 숙소
 *   3. 오늘 Day 가 없으면      출발 전이면 첫날, 끝났으면 마지막 날
 *   4. 아무것도 없으면         여행지 전체 (집결지 미정이면 그렇게 알립니다)
 *
 * 무엇으로 부르는지도 시점에 따라 다릅니다 — 지금 이곳 / 다음 장소 / 마지막 장소.
 * 출발 전에 마지막 날 장소를 '지금 이곳' 이라 부르면 거짓말이 됩니다.
 *
 * 단말 GPS 와는 무관합니다 — 저장된 좌표만 씁니다.
 */

import { useMemo } from 'react';
import { parseTimeToMinutes } from 'common/utils/datetime';
import type { DayView, DestinationInfo, GatherInfo, HotelInfo, PlaceItem } from './useTripView';

/** 미니지도가 지금 보여주는 것 */
export interface NowSpot {
    /** 무엇인지 — 라벨에 쓰는 구분 */
    kind: 'gather' | 'place' | 'next' | 'last' | 'hotel' | 'destination' | 'none';
    lat: number;
    lng: number;
    /** 장소 이름 */
    name: string;
    /** 언제인지 — '11/06 (금) 오후 6:00' 처럼 이미 다듬어진 문자열 */
    when: string | null;
    /** 누가 정했는지 */
    byNickname: string | null;
    /** 집결지가 아직 없어서 여행지를 대신 보여주는 중 */
    needsGather: boolean;
    /** 고른 것이 일정 장소면 그 원본 — 기록을 남길 때 placeId·sub_id 가 필요합니다.
     *  화면이 이름으로 장소를 다시 찾게 하면 같은 판단이 두 벌이 됩니다. */
    place?: PlaceItem | null;
    /** 고른 것이 그날 숙소일 때 */
    hotel?: HotelInfo | null;
    /** 다음에 갈 곳 — "제때 도착하나" 를 물을 목적지입니다.
     *  그날의 마지막 장소에 있거나 일정이 하나뿐이면 없습니다. */
    next?: PlaceItem | null;
    /** 다음 장소의 예정 시각 (HH:mm 등 원본 문자열) */
    nextTime?: string | null;
}

const num = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) && n !== 0 ? n : null;
};

const dayStartMs = (date: string): number => {
    const d = new Date(date);
    return Number.isNaN(d.getTime()) ? NaN : new Date(d).setHours(0, 0, 0, 0);
};

/** 그 장소가 그날 몇 분에 해당하는지 (시간이 없으면 null) */
const placeMinutes = (p: PlaceItem): number | null => parseTimeToMinutes(p.time);

interface Args {
    gather: GatherInfo | null;
    days: DayView[];
    destination: DestinationInfo | null;
    globalHotel: HotelInfo | null;
    /** 지금 시각 (ms) — 테스트와 재렌더를 위해 밖에서 받습니다 */
    nowMs: number;
    /** 날짜·시간을 보기 좋게 만드는 함수 — 화면이 i18n 을 쥐고 있으므로 주입받습니다 */
    formatWhen: (date: string | null, time: string | null) => string | null;
}

export const useNowSpot = ({
    gather, days, destination, globalHotel, nowMs, formatWhen,
}: Args): NowSpot => useMemo(() => {
    const firstDay = days[0] || null;
    const lastDay = days.length > 0 ? days[days.length - 1] : null;

    /* 1. 집결 — 여행 첫날의 집결 시각 전이면 집결지.
          시각을 못 읽으면 첫날이 시작되기 전까지를 집결 구간으로 봅니다. */
    if (gather) {
        const base = firstDay ? dayStartMs(firstDay.date) : NaN;
        const mins = parseTimeToMinutes(gather.time);
        const until = Number.isNaN(base)
            ? Infinity
            : base + (mins != null ? mins * 60_000 : 0);
        if (nowMs < until) {
            return {
                kind: 'gather',
                lat: gather.lat,
                lng: gather.lng,
                name: gather.name,
                when: formatWhen(firstDay?.date ?? null, gather.time),
                byNickname: gather.byNickname,
                needsGather: false,
            };
        }
    }

    /* 2. 여행 중 — 오늘에 해당하는 Day 를 찾고, 그 안에서 지금 시간대 장소를 고릅니다. */
    const todayStart = new Date(nowMs).setHours(0, 0, 0, 0);
    const today = days.find((d) => dayStartMs(d.date) === todayStart) || null;
    const nowMinutes = new Date(nowMs).getHours() * 60 + new Date(nowMs).getMinutes();

    /* 그 Day 가 오늘인지, 아직 안 왔는지, 지났는지 — 라벨이 달라집니다.
       이 구분이 없으면 출발 전에도 마지막 날 장소를 '지금 이곳' 이라고 말합니다. */
    const phaseOf = (day: DayView): 'today' | 'before' | 'after' => {
        const start = dayStartMs(day.date);
        if (Number.isNaN(start)) return 'today';
        if (start === todayStart) return 'today';
        return start > todayStart ? 'before' : 'after';
    };

    const pickFromDay = (day: DayView | null): NowSpot | null => {
        if (!day) return null;
        const phase = phaseOf(day);
        const withCoords = day.places.filter((p) => num(p.lat) != null && num(p.lng) != null);
        if (withCoords.length > 0) {
            /* 시간이 적힌 장소 중 지금을 지난 마지막 것. 없으면 그날 첫 장소. */
            const timed = withCoords
                .map((p) => ({ p, m: placeMinutes(p) }))
                .filter((x): x is { p: PlaceItem; m: number } => x.m != null)
                .sort((a, b) => a.m - b.m);
            const passed = phase === 'after'
                ? timed                                     // 지난 날이면 전부 지났습니다
                : phase === 'before' ? [] : timed.filter((x) => x.m <= nowMinutes);
            const chosen = passed.length > 0 ? passed[passed.length - 1].p : withCoords[0];

            /* 다음에 갈 곳 — 시각이 적힌 것 중 지금(혹은 고른 것) 다음 차례.
               시각이 없는 장소는 순서를 알 수 없어 목적지로 삼지 않습니다. */
            const chosenIdx = timed.findIndex((x) => x.p === chosen);
            const nextEntry = chosenIdx >= 0
                ? timed[chosenIdx + 1]
                : timed.find((x) => x.m > nowMinutes);
            /* 앞으로 갈 곳 / 지금 있을 곳 / 이미 지난 곳을 구분해 말합니다 */
            const kind: NowSpot['kind'] = phase === 'before'
                ? 'next'
                : phase === 'after' ? 'last' : (passed.length > 0 ? 'place' : 'next');
            return {
                kind,
                lat: num(chosen.lat)!,
                lng: num(chosen.lng)!,
                name: chosen.name,
                when: formatWhen(day.date, chosen.time ?? null),
                byNickname: chosen.byNickname ?? null,
                needsGather: false,
                place: chosen,
                next: nextEntry?.p ?? null,
                nextTime: nextEntry?.p.time ?? null,
            };
        }
        const hotel = day.hotel || globalHotel;
        if (hotel && num(hotel.lat) != null && num(hotel.lng) != null) {
            return {
                kind: 'hotel',
                lat: num(hotel.lat)!,
                lng: num(hotel.lng)!,
                name: hotel.name,
                when: formatWhen(day.date, null),
                byNickname: hotel.byNickname ?? null,
                needsGather: false,
                hotel,
            };
        }
        return null;
    };

    const fromToday = pickFromDay(today);
    if (fromToday) return fromToday;

    /* 3. 오늘에 해당하는 Day 가 없으면 — 아직 출발 전이면 첫날, 이미 끝났으면 마지막 날.
          늘 마지막 날을 보면 출발 전에도 여행 끝 장소가 떠 있었습니다. */
    const beforeTrip = !!firstDay && !Number.isNaN(dayStartMs(firstDay.date))
        && dayStartMs(firstDay.date) > todayStart;
    const fromEdge = pickFromDay(beforeTrip ? firstDay : lastDay);
    if (fromEdge) return fromEdge;

    /* 4. 아무것도 못 고르면 여행지 전체. 집결지가 없으면 그 사실을 알립니다. */
    if (destination) {
        return {
            kind: 'destination',
            lat: destination.latitude,
            lng: destination.longitude,
            name: destination.name,
            when: null,
            byNickname: null,
            needsGather: !gather,
        };
    }

    return {
        kind: 'none', lat: 0, lng: 0, name: '',
        when: null, byNickname: null, needsGather: !gather,
    };
}, [gather, days, destination, globalHotel, nowMs, formatWhen]);
